import { tileEquals, type TeamMap, type Tile } from '../types.js';
import { handPips } from './fivesScoring.js';

/** Козёл specials: a hand left holding only these doubles counts a fixed value. */
export const LONE_DOUBLE_BLANK_VALUE = 25;
export const LONE_DOUBLE_SIX_VALUE = 50;
export const DOUBLE_BLANK_AND_SIX_VALUE = 75;

export const DOUBLE_BLANK: Tile = [0, 0];
export const DOUBLE_SIX: Tile = [6, 6];

function holdsOnly(hand: readonly Tile[], wanted: readonly Tile[]): boolean {
  return hand.length === wanted.length && wanted.every((w) => hand.some((t) => tileEquals(t, w)));
}

/** Penalty value of a seat's remaining hand: its pips, except the 25/50/75 specials. */
export function handValue(hand: readonly Tile[]): number {
  if (holdsOnly(hand, [DOUBLE_BLANK])) return LONE_DOUBLE_BLANK_VALUE;
  if (holdsOnly(hand, [DOUBLE_SIX])) return LONE_DOUBLE_SIX_VALUE;
  if (holdsOnly(hand, [DOUBLE_BLANK, DOUBLE_SIX])) return DOUBLE_BLANK_AND_SIX_VALUE;
  return handPips(hand);
}

function teamOf(teamMap: TeamMap, seat: number): number {
  return teamMap.teamOf[seat] ?? seat;
}

/** Per-team sum of `perSeat[seat]` (FFA: every seat is its own team). */
export function sumByTeam(perSeat: readonly number[], teamMap: TeamMap): number[] {
  const totals = new Array<number>(teamMap.teamCount).fill(0);
  perSeat.forEach((value, seat) => {
    const team = teamOf(teamMap, seat);
    totals[team] = (totals[team] ?? 0) + value;
  });
  return totals;
}

/** Per-team sum of every seat's `handValue`. */
export function teamHandValues(hands: readonly (readonly Tile[])[], teamMap: TeamMap): number[] {
  return sumByTeam(hands.map(handValue), teamMap);
}

export function seatsOfTeam(teamMap: TeamMap, team: number): number[] {
  const seats: number[] = [];
  for (let seat = 0; seat < teamMap.teamOf.length; seat += 1) {
    if (teamOf(teamMap, seat) === team) seats.push(seat);
  }
  return seats;
}

/**
 * Seat a team's score is recorded against — its lowest seat index, the same
 * convention fives scoring uses for partnership awards.
 */
export function recordSeatOf(teamMap: TeamMap, team: number): number {
  return seatsOfTeam(teamMap, team)[0] ?? team;
}
